import { Suspense, useMemo } from 'react';
import { SERVICES } from '../shell/services';
import { useShell } from '../shell/ShellContext';
import { SpaceSwitcher } from '../shell/SpaceSwitcher';
import { useIsDesktop } from '../shell/useMediaQuery';
import { ActivityCard, ActivityList } from './cards/ActivityCard';
import { EmptyHome, HomeSkeleton } from './cards/HomeStates';
import { ServiceCard } from './cards/ServiceCard';
import { ShortcutsCard, TreasuryCard, UpcomingCard } from './cards/StaticCards';
import type { HomeState } from './data/types';
import { Doodle } from './Doodle';
import { useHomeData } from './useHomeData';
import './home.css';

/** Carte d'un service dont le module fournit de vraies données (Mots de passe, Notes), le temps de son chargement. */
function SlotFallback({ title }: { title: string }) {
  return (
    <ServiceCard title={title} busy>
      <div className="lh-skeleton" style={{ width: '60%', height: 16 }} />
      <div className="lh-skeleton" style={{ width: '45%', height: 16 }} />
    </ServiceCard>
  );
}

/**
 * Page Accueil (maquette 2a) : salutation, une carte résumé par service, raccourcis et activité.
 * `state` impose l'état affiché (chargement, vide) ; hors démonstration il reste « normal ».
 */
export function HomePage({ state = 'normal' }: { state?: HomeState }) {
  const { user, session } = useShell();
  const desktop = useIsDesktop();
  const data = useHomeData();
  const now = useMemo(() => new Date(), []);

  const real = SERVICES.filter((s) => s.summary).map((s) => {
    const Summary = s.summary!;
    return (
      <Suspense key={s.id} fallback={<SlotFallback title={s.label} />}>
        <Summary session={session} />
      </Suspense>
    );
  });

  return (
    <div className="hm lh-stagger" data-layout={desktop ? 'desktop' : 'mobile'}>
      {desktop ? <Doodle /> : null}
      {desktop ? null : <SpaceSwitcher variant="mobile" />}
      <h1 className="hm__hello">Bonjour {user.name}</h1>

      {state === 'loading' ? (
        <HomeSkeleton />
      ) : state === 'empty' ? (
        <EmptyHome />
      ) : (
        <>
          <div className="hm-grid">
            {real}
            <TreasuryCard data={data.treasury} />
            <UpcomingCard data={data.upcoming} now={now} />
          </div>
          <ShortcutsCard />
          {desktop ? <ActivityCard entries={data.activity} now={now} /> : <ActivityList entries={data.activity} now={now} />}
        </>
      )}
    </div>
  );
}
